import { Component } from '@angular/core';
import { BaseComponent } from '../../base.component';
import { FlatTreeControl } from '@angular/cdk/tree';
import { FolderNode } from '../../common/folder-tree-view/folder-node';
import { FolderDataSource } from '../../common/folder-tree-view/folder-data-source';
import { FolderSdk } from '../../../sdks/folder.sdk';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { OkCancelDialogComponent } from '../../dialogs/ok-cancel/ok-cancel-dialog.component';
import { filter, switchMap } from 'rxjs';
import { AddEditFolderDialogComponent } from './add-edit-folder/add-edit-folder-dialog.component';
import { Folder } from '../../../models/ui/folder.model';
import { BuiltInFieldId } from '../../../models/built-in-field-id.enum';
import { FieldDisplayType } from '../../dialogs/select-filter-new/models/field-display-type';
import { BrowseManager } from '../../../managers/browse.manager';
import { ImportFoldersDialogComponent } from './import-folders-dialog/import-folders-dialog.component';

@Component({
    selector: 'lex-folders',
    templateUrl: './folders.component.html',
    styleUrls: ['./folders.component.scss'],
    standalone: false
})
export class FoldersComponent extends BaseComponent {
	public treeControl: FlatTreeControl<FolderNode>;
	public dataSource: FolderDataSource;
	public isAnyNodeSelected = false;
	public isNodeProtected = false;

	constructor(private folderService: FolderSdk, private dialog: MatDialog, private browseManager: BrowseManager) {
		super();
		this.treeControl = new FlatTreeControl<FolderNode>(this.getLevel, this.isExpandable);
		this.dataSource = new FolderDataSource(this.treeControl, folderService);
		this.refreshNodeData();
	}

	public getLevel = (node: FolderNode) => node.level;

	public isExpandable = (node: FolderNode) => node.expandable;

	public hasChild = (_: number, node: FolderNode) => node.expandable;

	public folderClick(node: FolderNode) {
		this.dataSource.data.forEach(d => {
			d.selected = false;
		});
		node.selected = true;
		this.isAnyNodeSelected = true;
		this.isNodeProtected = node.isCaseRootNode || node.folder.isDefaultType;
		this.goToFolder(node.folder);
	}

	public goToFolder(folder: Folder): void {
		this.browseManager.setBrowseFilter(this.browseManager.createOrUpdateFilter(BuiltInFieldId.Folder, FieldDisplayType.Text, [folder.folderId]));
	}

	public importFolders(): void {
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';

		this.dialog.open(ImportFoldersDialogComponent, dialogConfig).afterClosed()
			.subscribe(() => {
				this.refreshNodeData();
			});
	}

	public addFolder(event: any, parentNode: FolderNode) {
		event.stopPropagation();
		const folder = new Folder();
		folder.parentFolderId = parentNode.folder.folderId;

		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = {
			folder
		};

		this.dialog.open(AddEditFolderDialogComponent, dialogConfig).afterClosed()
			.pipe(
				filter((folder) => folder),
				switchMap((folder) => this.folderService.post(folder))
			)
			.subscribe(() => {
				this.refreshNodeData();
			});
	}

	public editFolder(event: any) {
		event.stopPropagation();
		const folder = this.getSelectedNode()?.folder;

		if (!folder || this.isNodeProtected) return;

		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.width = '500px';
		dialogConfig.data = {
			folder
		};

		this.dialog.open(AddEditFolderDialogComponent, dialogConfig).afterClosed()
			.pipe(
				filter((folder) => folder),
				switchMap((folder) => this.folderService.put(folder))
			)
			.subscribe(() => {
				this.refreshNodeData();
			});
	}

	public removeFolder(event: any) {
		event.stopPropagation();
		if (this.isNodeProtected)
			return;
		const dialogConfig = new MatDialogConfig();
		dialogConfig.autoFocus = true;
		dialogConfig.maxWidth = '500px';
		dialogConfig.data = {
			title: `Delete Selected Folder`,
			message: `Delete Selected Folder [${this.getSelectedNode().folder.folderName}] And Its SubFolders? This will <strong>PERMANENTLY DELETE</strong> these folders from the entire case. Any documents in these folders will be moved to the <strong>Unassigned</strong> folder. This action cannot be undone.`,
		};

		this.dialog.open(OkCancelDialogComponent, dialogConfig).afterClosed()
			.pipe(
				filter((okClicked) => okClicked),
				switchMap(() => this.folderService.delete(this.getSelectedNode().folder))
			)
			.subscribe(() => {
				this.refreshNodeData();
			});

	}

	public refreshNodeData(): void {
		this.folderService.getRootFolder().subscribe(data => {
			const root = new FolderNode(data.value[0], 0);
			this.dataSource.data = [root];
			this.treeControl.expand(root); // Immediately expand the root (and load its children)
			this.isNodeProtected = true;
			this.isAnyNodeSelected = false;
		});
	}

	private getSelectedNode(): FolderNode {
		return this.dataSource.data.find((node) => node.selected);
	}
}
